
import { jwtDecode } from 'jwt-decode';
import { User } from '@/utils/types';
import { applyWebsiteScopeHeaders } from '@/lib/websiteScope';
import { PUBLIC_API_BASE_URL } from '@/utils/publicApiConfig';

// JWT payload type definition
interface JwtPayload {
  /** Expiration time – seconds since epoch (standard JWT claim) */
  exp: number;
  /** Issue‑at time – seconds since epoch (optional) */
  iat?: number;
  /** Custom user claims that your backend embeds in the token */
  sub?: string;
  email?: string;
  username?: string;
  [key: string]: unknown;
}

/**
 * Decodes a JWT token into a payload object
 */
export const decodeJwt = (token: string): JwtPayload => jwtDecode<JwtPayload>(token);

/**
 * Checks if a token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = decodeJwt(token);
    return Date.now() >= exp * 1000;
  } catch {
    // If decoding fails we treat the token as invalid / expired
    return true;
  }
};

/**
 * Extracts user information from a JWT token
 */
export const extractUserFromToken = (token: string): User | null => {
  try {
    const payload = decodeJwt(token);
    const { sub: id, email, username, name, ...rest } = payload;
    return { id: id ?? '', email, username, name, ...rest } as User;
  } catch {
    return null;
  }
};

/**
 * Verify token validity by calling the API
 */
export const verifyToken = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${PUBLIC_API_BASE_URL}/token/verify/`, {
      method: 'POST',
      headers: applyWebsiteScopeHeaders({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({ token })
    });
    
    return response.ok;
  } catch (error) {
    console.error("Token verification failed:", error);
    return false;
  }
};
